import styles from './ColumnTogglePanel.module.css'

import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { OverlayPanel } from 'primereact/overlaypanel'

export interface ToggleableColumn {
  field: string
  label: string
}

interface Props {
  toggleableColumns: ToggleableColumn[]
  visibleFields: string[]
  pendingFields: string[]
  setPendingFields: React.Dispatch<React.SetStateAction<string[]>>
  onConfirm: (fields: string[]) => void
}

export const ColumnTogglePanel: React.FC<Props> = ({
  toggleableColumns,
  visibleFields,
  pendingFields,
  setPendingFields,
  onConfirm,
}) => {
  const { t } = useTranslation()
  const overlayRef = useRef<OverlayPanel>(null)

  return (
    <>
      <OverlayPanel
        ref={overlayRef}
        onShow={() => setPendingFields(visibleFields)}
      >
        <div className={`flex flex-column ${styles.columnTogglePanel}`}>
          <div className={`pb-2 ${styles.columnToggleTitle}`}>
            <span className="font-bold text-lg">
              {t('common.columnSelection')}
            </span>
          </div>
          <div
            className={`flex align-items-center gap-2 py-2 ${styles.columnToggleHeader}`}
          >
            <Checkbox
              inputId="toggle-all"
              checked={pendingFields.length === toggleableColumns.length}
              onChange={e => {
                if (e.checked) {
                  setPendingFields(toggleableColumns.map(col => col.field))
                } else {
                  setPendingFields([])
                }
              }}
            />
            <label
              htmlFor="toggle-all"
              className="cursor-pointer font-semibold"
            >
              {t('common.selectAll')}
            </label>
          </div>
          <div className={styles.columnToggleList}>
            {toggleableColumns.map(col => (
              <div
                key={col.field}
                className="flex align-items-center gap-2 py-2"
              >
                <Checkbox
                  inputId={col.field}
                  checked={pendingFields.includes(col.field)}
                  onChange={e => {
                    if (e.checked) {
                      setPendingFields(prev => [...prev, col.field])
                    } else {
                      setPendingFields(prev =>
                        prev.filter(f => f !== col.field),
                      )
                    }
                  }}
                />
                <label htmlFor={col.field} className="cursor-pointer">
                  {col.label}
                </label>
              </div>
            ))}
          </div>
          <div className={`flex gap-2 pt-2 mt-2 ${styles.columnToggleFooter}`}>
            <Button
              label={t('common.confirm')}
              size="small"
              className="flex-1"
              onClick={() => {
                onConfirm(pendingFields)
                overlayRef.current?.hide()
              }}
            />
            <Button
              label={t('common.cancel')}
              size="small"
              outlined
              className="flex-1"
              onClick={() => overlayRef.current?.hide()}
            />
          </div>
        </div>
      </OverlayPanel>
      <Button
        icon="pi pi-cog"
        aria-label={t('common.settings')}
        text
        rounded
        plain
        onClick={e => {
          e.stopPropagation()
          overlayRef.current?.toggle(e)
        }}
      />
    </>
  )
}
